'use client'

// Sign up — the standing /signup page (the landing's "begin" flow opens the same
// language in place; this page serves direct links). Left-aligned auth column,
// the ever-changing netti on the right, OAuth + the email magic link. The CTA is
// "begin" and its badge is netti itself — entering the innernet experience.
//
// ── THE TURN (law_always_there) ───────────────────────────────────────────────
// When the address someone types already has an innernet behind it, this page
// does not send them anywhere. The sign-in was always here; it is UNCOVERED.
// The heading, the lockup's suffix, the form and the closing line resolve into
// the returning-person versions of themselves — opacity and blur on the house
// easing, nothing translating, no navigation — and the address bar is corrected
// underneath with `history.replaceState`, so a reload lands where you are.
//
// The recognition itself is deliberately cheap to design and expensive to abuse:
// it happens on the SUBMIT, never while typing, and the one bit it needs comes
// from `/api/auth/begin`, which is rate-limited and fail-closed (the full
// reasoning, including what it does and does not disclose, is in that route).
// Anything other than a clear "yes" — a slow server, no service key, a limiter
// refusing — falls straight back to the ordinary signup magic link.

import React, { Suspense, useEffect, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import OAuthButtons from '../../components/OAuthButtons'
import InAppBrowserNotice from '../../components/InAppBrowserNotice'
import InAppHint from '../../components/InAppHint'
import EmailCodeEntry from '../../components/EmailCodeEntry'
import AuthShell, { AuthHeading, PasswordField, authAtoms as a } from '../../components/AuthShell'
import DoorMark from '../../components/brand/DoorMark'
import { Field, PillButton } from '../../components/ui'
import { createClient } from '../../lib/supabase/client'
import { siteUrl } from '../../lib/site-url'
import { NETWORK_LINE, friendlyAuthError, friendlyThrown, isNetworkError } from '../../lib/auth-errors'
import { authFailReason } from '../../lib/auth-reason'
import { trackFunnel, EVENTS } from '../../lib/analytics'

export default function SignupPage() {
  return (
    <Suspense fallback={null}>
      <SignupInner />
    </Suspense>
  )
}

/** 'begin' — a stranger at the door. 'welcome' — the door recognised them. */
type Mode = 'begin' | 'welcome'

function SignupInner() {
  const searchParams = useSearchParams()
  // ⚠️ WHERE THIS DOOR WAS OPENED FROM IS READ ONCE, at the first render, and kept.
  // The turn below corrects the address bar with history.replaceState, which Next
  // folds back into `useSearchParams` — so a `dest` derived from the live params
  // forgot `?from=skill:x` the moment the page turned to "welcome back", and the
  // person who signed in landed on a bare dashboard instead of with their skill.
  const [entry] = useState(() => {
    // opened from the desktop app → route the finished session back to it.
    const fromApp = searchParams.get('source') === 'app'
    // arrived holding a skill (`?from=skill:<name>` / `?from=share:<token>`, from a
    // skill page's gateway) → the finished session lands on the dashboard with the
    // same skill in hand, so "save it to your innernet" completes after signing up.
    const from = searchParams.get('from')
    const handed = from && /^(skill|share):[A-Za-z0-9_.-]+$/.test(from) ? from : null
    const v = searchParams.get('v')
    const version = v && /^[A-Za-z0-9_.-]+$/.test(v) ? v : null
    return { fromApp, handed, version }
  })
  const { fromApp, handed, version } = entry
  const dest = fromApp
    ? '/auth/handoff'
    : handed
      ? `/dashboard?add=${encodeURIComponent(handed)}${version ? `&v=${encodeURIComponent(version)}` : ''}`
      : '/dashboard'
  // the sign-in door that carries the same hand-off, and the address of this one
  const loginHref = fromApp ? '/login?source=app' : dest !== '/dashboard' ? `/login?next=${encodeURIComponent(dest)}` : '/login'
  const signupHref = (() => {
    const q = new URLSearchParams()
    if (fromApp) q.set('source', 'app')
    if (handed) q.set('from', handed)
    if (handed && version) q.set('v', version)
    const qs = q.toString()
    return `/signup${qs ? `?${qs}` : ''}`
  })()
  const [mode, setMode] = useState<Mode>('begin')
  // the first paint is not a turn — `.auth-body` already settles the whole
  // column in. `.auth-turn` only ever plays on a door that CHANGED its mind.
  const [turns, setTurns] = useState(0)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)
  const [sending, setSending] = useState(false)

  // The landing's begin flow fires this in place; this page is the same door
  // reached by a direct link (a share, an email, the desktop app), so it reports
  // the same top-of-funnel moment — otherwise every direct arrival looks like a
  // signup that came from nowhere.
  useEffect(() => {
    trackFunnel(EVENTS.begin_opened, { entry: 'signup_page', from_app: fromApp })
  }, [fromApp])

  // OAuth leaves the page; the refusal is the only part we ever get to see.
  const oauthFailed = (msg: string) => {
    // the buttons' own sentences pass through; the browser's "Failed to fetch" does not
    setError(isNetworkError(msg) ? NETWORK_LINE : msg)
    trackFunnel(EVENTS.auth_failed, { method: 'oauth', reason: authFailReason(msg), intent: mode === 'welcome' ? 'signin' : 'signup' })
  }

  /** The one bit: does this address already have an innernet? Never throws, and
   *  never blocks the flow — an unclear answer is "no". */
  const alreadyInside = async (addr: string): Promise<boolean> => {
    try {
      const res = await fetch('/api/auth/begin', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: addr }),
      })
      if (!res.ok) return false
      const data = (await res.json()) as { known?: boolean }
      return data?.known === true
    } catch {
      return false
    }
  }

  /** The uncovering. Nothing navigates; the URL is corrected underneath so a
   *  reload lands on the door the page has actually become. */
  const turnToWelcome = () => {
    setMode('welcome')
    setTurns((t) => t + 1)
    setError('')
    setSent(false)
    try {
      // the hand-off rides along as `next` (the same shape /login reads), so a
      // reload of the corrected address still lands on the skill, not a bare dashboard
      window.history.replaceState(null, '', loginHref)
    } catch {
      /* an embedded browser that refuses history — the surface is still right */
    }
    // the tab says what the page became (replaceState carries no metadata)
    document.title = 'sign in · innernet'
    trackFunnel(EVENTS.begin_opened, { entry: 'signup_page', from_app: fromApp, recognised: true })
  }

  /** back to a blank door — for the person whose address simply isn't theirs. */
  const turnToBegin = () => {
    setMode('begin')
    setTurns((t) => t + 1)
    setError('')
    setPassword('')
    try {
      window.history.replaceState(null, '', signupHref)
    } catch {
      /* as above */
    }
    document.title = 'create account · innernet'
  }

  const sendMagicLink = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    if (!email) {
      setError('enter your email first.')
      trackFunnel(EVENTS.error_shown, { reason: 'email_missing', method: 'magic_link' })
      return
    }
    setSending(true); setError(''); setSent(false)

    // the door looks first. a person who already has an innernet is shown it,
    // not mailed a second welcome.
    if (mode === 'begin' && (await alreadyInside(email))) {
      setSending(false)
      turnToWelcome()
      return
    }

    try {
      const supabase = createClient()
      const { error: err } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: `${siteUrl()}/auth/land?next=${encodeURIComponent(dest)}` },
      })
      if (err) {
        setError(friendlyAuthError(err.message))
        trackFunnel(EVENTS.auth_failed, { method: 'magic_link', reason: authFailReason(err.message), intent: mode === 'welcome' ? 'signin' : 'signup' })
        setSending(false); return
      }
      setSent(true)
      // the credential is out the door — completion lands in the inbox, often on
      // another device, so the gap to `signup_completed` is the mail funnel.
      trackFunnel(EVENTS.signup_started, { method: 'magic_link', intent: mode === 'welcome' ? 'signin' : 'signup' })
    } catch (err) {
      setError(friendlyThrown(err))
      trackFunnel(EVENTS.auth_failed, { method: 'magic_link', reason: authFailReason(String(err)), intent: mode === 'welcome' ? 'signin' : 'signup' })
    }
    setSending(false)
  }

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setSending(true); setError('')
    try {
      const supabase = createClient()
      const { error: err } = await supabase.auth.signInWithPassword({ email, password })
      if (err) {
        setError(friendlyAuthError(err.message))
        // only the failure fires here — the success is AnalyticsIdentity's job
        // (it watches for the real session), so `signed_in` can never double-count.
        trackFunnel(EVENTS.auth_failed, { method: 'password', reason: authFailReason(err.message) })
        setSending(false); return
      }
      window.location.href = dest
    } catch (err) {
      setError(friendlyThrown(err))
      trackFunnel(EVENTS.auth_failed, { method: 'password', reason: authFailReason(String(err)) })
      setSending(false)
    }
  }

  const welcome = mode === 'welcome'
  /** the reveal class, only once the surface has actually turned. */
  const turn = turns ? 'auth-turn' : undefined
  const turned = (base: string) => (turns ? `${base} auth-turn` : base)

  return (
    <AuthShell
      suffix={welcome ? 'sign in' : 'begin'}
      heading={
        welcome ? (
          <AuthHeading key={`welcome-${turns}`} className={turn} lead="your memory is waiting," accent="welcome back." />
        ) : (
          <AuthHeading key={`begin-${turns}`} className={turn} lead="one memory of you," accent="carried across every ai." />
        )
      }
    >
      <InAppBrowserNotice />
      <OAuthButtons
        key={`oauth-${mode}-${turns}`}
        className={turn}
        next={dest}
        verb={welcome ? 'continue with' : 'begin with'}
        layout="row"
        onError={oauthFailed}
      />
      <InAppHint />

      {error ? <p style={a.err}>{error}</p> : null}

      {sent ? (
        <div className="view-fade" style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
          <p style={{ color: 'var(--ink-2)', fontSize: '0.92rem', lineHeight: 1.6, margin: 0, textWrap: 'pretty' }}>
            check your email. one tap on the link signs you in, or type the code here.
          </p>
          <EmailCodeEntry email={email} next={dest} onChangeAddress={() => { setSent(false); setError('') }} />
        </div>
      ) : welcome ? (
        // the sign-in that was here all along: the address carries over, the
        // password is the only new thing being asked for.
        <form key={`pw-${turns}`} onSubmit={submitPassword} className={turned('begin-magic')}>
          <div className="begin-or"><span>the innernet you already have</span></div>
          <Field label="your email" type="email" inputMode="email" autoComplete="email"
            placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          <PasswordField label="password" value={password} onChange={(e) => setPassword(e.target.value)}
            required autoComplete="current-password" placeholder="your password" autoFocus
            aside={<a href="/forgot-password" className="auth-aside-link">forgot password?</a>} />
          <PillButton type="submit" className="cta-pill auth-door btn-pill--solid" loading={sending} icon={<DoorMark />}>
            {sending ? 'entering…' : 'enter'}
          </PillButton>
          <p style={{ ...a.quiet, margin: 0 }}>
            or{' '}
            <button
              type="button"
              onClick={sendMagicLink}
              style={{ ...a.quiet, appearance: 'none', background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--ink)', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '4px' }}
            >
              send a link to your email
            </button>
            {' '}instead.
          </p>
        </form>
      ) : (
        <form key={`begin-${turns}`} onSubmit={sendMagicLink} className={turned('begin-magic')}>
          <div className="begin-or"><span>or with your email</span></div>
          <Field label="your email" type="email" inputMode="email" autoComplete="email"
            placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          {/* entering the innernet experience — the badge is netti itself */}
          <PillButton type="submit" className="cta-pill auth-door btn-pill--solid" loading={sending} icon={<DoorMark />}>
            {sending ? 'sending…' : 'begin'}
          </PillButton>
        </form>
      )}

      {welcome ? (
        <p key={`foot-welcome-${turns}`} className={turned('auth-foot')}>
          not your address?{' '}
          <button
            type="button"
            onClick={turnToBegin}
            style={{ ...a.quiet, appearance: 'none', background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--ink)', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '4px' }}
          >
            begin a new innernet
          </button>
        </p>
      ) : (
        <p key={`foot-begin-${turns}`} className={turned('auth-foot')}>
          already inside? <a href={loginHref}>sign in</a>
        </p>
      )}
    </AuthShell>
  )
}
